import { labels } from "./labels"
import { PLACMENT_STATUS_VALUES } from "./values"
import { getIndexDBconfig } from "./IndexDBConfig.js"
import moment from "moment"
import ExcelJS from "exceljs"
import { random_colors } from "./colors"
import { PERMISSIONS } from "./permissions"

export const capitalizeFirstLetter = (string) => {
    return string && string.length
        ? `${string.charAt(0).toUpperCase()}${string.slice(1).toLowerCase()}`
        : ""
}

export const getPlanStatusArray = (status) => {
    const planStatus = status ? status.toUpperCase() : ""
    if (planStatus === "E") return [labels.common.empty]
    else if (planStatus === "L") return [labels.common.load]
    else if (planStatus === "EL" || planStatus === "LE")
        return [labels.common.load, labels.common.empty]
    else return []
}
export const setPlanStatus = (planStatus) => {
    if (planStatus.length !== 0) {
        if (planStatus.length === 1 && planStatus[0] === labels.common.load)
            return "L"
        else if (
            planStatus.length === 1 &&
            planStatus[0] === labels.common.empty
        )
            return "E"
        else return "LE"
    } else return ""
}

/**
 * This function is used to get the plan type in the format which is required by the API.
 *
 * @param {*} planType - The plan type which is selected by the user in the UI. It can be either "Equipment Type" or "Commodity".
 * @returns {string} The plan type in the format required by the API.
 */
export const getPlanType = (planType) => {
    if (planType === labels.planLbl.equipmentType) {
        return "EQUIPMENT"
    } else if (planType === labels.planLbl.commodity) {
        return "COMMODITY"
    }
    return planType
}
/**
 * Prepares the commodity details for creating a new commodity plan.
 * @param {Object} commodityTypePlanSummary - The summary of the commodies plan which contains details like commodity, quantity, unit, origin, destination, ETA window and hazardous information.
 * @returns {Object} The commodity info for the API call.
 */
export const prepareCommodityPlanRequestBody = (
    commodityTypePlanSummary = {}
) => {
    const commodityInfo = Object.values(commodityTypePlanSummary)[0]
    const {
        commodity,
        quantity,
        unit,
        origin,
        destination,
        targetETAWindow: { startDate, endDate } = {},
        hazardous,
    } = commodityInfo

    return {
        commodity: commodity || "",
        quantity: parseFloat(quantity) || 0,
        metrics: unit || "",
        origin: origin || "",
        destination: destination || "",
        fromEta: moment(startDate).format("YYYY-MM-DD") || "",
        toEta: moment(endDate).format("YYYY-MM-DD") || "",
        isHazardous: hazardous || false,
    }
}
/**
 * Checks if the given plan is a commodity plan.
 * @param {*} plan - The plan object to check.
 * @returns {boolean} True if the plan is a commodity plan, false otherwise.
 */
export const isCommodityPlan = (plan) => plan.planType === "COMMODITY"

/**
 * Checks if the given plan is an equipment plan.
 * @param {*} plan - The plan object to check.
 * @returns {boolean} True if the plan is an equipment plan, false otherwise.
 */
export const isEquipmentPlan = (plan) => plan.planType === "EQUIPMENT"

/**
 *  Gets the default plan from the list of all plans. If a planType is provided, it returns the default plan for that type.
 *  If no planType is provided, it prefers to return a default commodity plan, else a default equipment plan, and
 * if neither exists, it returns the first plan in the list.
 * @param {*} allPlans
 * @param {*} planType
 * @returns {Object|null} The default plan object if found, otherwise null.
 */
export const getDefaultPlan = (allPlans, planType = "") => {
    if (!allPlans.length) return null

    // If planType provided, return default plan for that type only
    if (planType) {
        const plansOfType = allPlans.filter(
            (plan) => plan.planType === planType
        )
        const defaultPlan = plansOfType.find((plan) => plan.default === true)
        return defaultPlan || null
    }

    // Prefer default commodity plan, else default equipment plan
    const defaultCommodityPlan = allPlans.find(
        (plan) => plan.planType === "COMMODITY" && plan.default === true
    )
    if (defaultCommodityPlan) return defaultCommodityPlan

    const defaultEquipmentPlan = allPlans.find(
        (plan) => plan.planType === "EQUIPMENT" && plan.default === true
    )
    if (defaultEquipmentPlan) return defaultEquipmentPlan

    return allPlans[0] || null
}
/**
 * Normalizes the commodities data to ensure it is always an array .
 * we are doing it because currently the data is single commodity and it is a object.
 * @param {*} commodities - The commodities data to normalize.
 * @returns {Array} The normalized commodities array.
 */
export const normalizePlanCommodities = (commodities) => {
    if (Array.isArray(commodities)) {
        return commodities
    }
    return commodities ? [commodities] : []
}

/**
 * Gets the unique values from a plan based on its type.
 * @param {*} plan - The plan object to extract unique values from.
 * @returns {Object} An object containing the type and unique values from the plan.
 */
export const getUniquePlanValues = (plan) => {
    if (!plan) {
        return { type: 0, value: [] }
    }

    if (isCommodityPlan(plan)) {
        const commodityNames = normalizePlanCommodities(
            plan.railCarPlanCommodity
        )
            .map((item) => item?.commodity ?? item)
            .filter(Boolean)
        return { type: 2, value: Array.from(new Set(commodityNames)) }
    }

    if (isEquipmentPlan(plan)) {
        return { type: 1, value: Array.from(new Set(plan.carTypes || [])) }
    }

    return { type: 0, value: [] }
}

export const sortbyKey = (arrayData, key) => {
    return arrayData.sort((originalArray, comparisonArray) => {
        var originalArrayKey = originalArray[key]
        var comparisonArrayKey = comparisonArray[key]
        return originalArrayKey > comparisonArrayKey
            ? -1
            : originalArrayKey > comparisonArrayKey
              ? 1
              : 0
    })
}
export const sortAlphabetically = (arrayData, key) => {
    return arrayData.sort((originalArray, comparisonArray) => {
        var originalArrayKey = originalArray[key].toLowerCase()
        var comparisonArrayKey = comparisonArray[key].toLowerCase()
        return originalArrayKey !== comparisonArrayKey
            ? originalArrayKey < comparisonArrayKey
                ? -1
                : 1
            : 0
    })
}
/*
  Returns the index of the option which start with a specific key word.
*/
export const optionStartWith = (options, key) => {
    if (options.length && key) {
        for (let i = 0; i < options.length; i++) {
            if (options[i].startsWith(key)) {
                return i.toString()
            }
        }
    }
}

export const getPlacementStatusValue = (type) =>
    PLACMENT_STATUS_VALUES[type].value

export const getPlacementStatusByValue = (value) => {
    let status = ""
    const placementKeys = Object.keys(PLACMENT_STATUS_VALUES)
    placementKeys.forEach((key) => {
        if (PLACMENT_STATUS_VALUES[key].value.toString() === value.toString()) {
            status = PLACMENT_STATUS_VALUES[key].label
        }
    })

    return status
}

const formatExportMonthDay = (date) => {
    const monthLabel = labels.monthsInitials[moment(date).month()]
    const dayLabel = moment(date).format("DD")

    return `${monthLabel} ${dayLabel}`
}
/**
 * Exports the calendar view data to a CSV file for download. The CSV includes the selected plan name, visible date range, and weekly metrics for each day in the month range.
 * @param {{ monthRange: Object, selectedPlanName: string }} param0 - The export parameters.
 * @returns {void}
 */
export const downloadCSV = ({ monthRange, selectedPlanName }) => {
    if (!monthRange?.monthDetails || !monthRange.monthDetails.total35Days)
        return

    const days = monthRange.monthDetails.total35Days
    const { monthStart, monthStartDay, monthEnd, monthEndDay } =
        monthRange.monthDetails
    const lines = []
    // Report header rows: selected plan and visible date range.
    lines.push(`${labels.planLbl.railCarPlan},${selectedPlanName}`)
    lines.push(
        `${labels.common.dateRange},${monthStart} ${monthStartDay} - ${monthEnd} ${monthEndDay}`
    )
    lines.push("")

    // Main table header followed by day columns.
    lines.push(
        `${labels.common.week},${labels.common.status},${labels.common.metric},${labels.weekDays.join(",")}`
    )

    // Per-week metrics exported under each date row.
    const metrics = [
        { label: labels.common.onHand, key: "TotalOnHand" },
        { label: labels.common.inBound, key: "TotalInbound" },
        { label: labels.common.planned, key: "planCount" },
        { label: labels.common.projected, key: "projected_cars" },
    ]

    for (let weekIndex = 0; weekIndex < 5; weekIndex++) {
        const week = days.slice(weekIndex * 7, weekIndex * 7 + 7)
        // Week status is driven by any negative projected value in that week.
        const hasAlert = week.some((d) => d.projected_cars < 0)
        const weekStatus = hasAlert
            ? labels.planLbl.review
            : labels.planLbl.good

        const dates = week.map((d) => formatExportMonthDay(d.CreateTime))
        lines.push(
            `${labels.common.week} ${weekIndex + 1},${weekStatus},${labels.common.date},${dates.join(",")}`
        )

        metrics.forEach(({ label, key }) => {
            const vals = week.map((d) => d[key] ?? 0)
            lines.push(`,,${label},${vals.join(",")}`)
        })

        // Blank line separates each week block in the CSV output.
        lines.push("")
    }

    // Prefix BOM so Excel opens UTF-8 content correctly.
    const blob = new Blob(["\uFEFF" + lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    // Trigger browser download without navigating away from the page.
    link.setAttribute(
        "download",
        `${labels.planLbl.calendarView}-${selectedPlanName.toLowerCase().replace(/\s+/g, "")}-${moment().format("YYYYMMDD")}.csv`
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

/**
 * Exports the calendar view data to a styled XLSX workbook.
 * The sheet includes a header, date range, week blocks, and metric rows
 * with conditional colors for status and projected alerts.
 * @param {{ monthRange: Object, selectedPlanName: string }} param0
 * @returns {Promise<void>}
 */
export const downloadXLS = async ({ monthRange, selectedPlanName }) => {
    if (!monthRange?.monthDetails || !monthRange.monthDetails.total35Days)
        return

    const days = monthRange.monthDetails.total35Days
    const { monthStart, monthStartDay, monthEnd, monthEndDay } =
        monthRange.monthDetails

    const C_BG = { argb: "FFF7F7FB" }
    const C_PAST = { argb: "FFE8ECF1" }
    const C_WHITE = { argb: "FFFFFFFF" }
    const C_PLANNED_BG = { argb: "FFC7D4DD" }
    const C_PROJECTED_BG = { argb: "FF57657A" }
    const C_TODAY_BG = { argb: "FF508BE4" }
    const C_GREEN = { argb: "FF7FAE1B" }
    const C_RED = { argb: "FFF34336" }
    const C_BLACK = { argb: "FF000000" }
    const C_GREY = { argb: "FF7A7A7A" }
    const C_BLUE = { argb: "FF508BE4" }
    const C_WHITE_TXT = { argb: "FFFFFFFF" }

    const borderThin = {
        top: { style: "thin", color: { argb: "FFC0C5CC" } },
        left: { style: "thin", color: { argb: "FFC0C5CC" } },
        bottom: { style: "thin", color: { argb: "FFC0C5CC" } },
        right: { style: "thin", color: { argb: "FFC0C5CC" } },
    }

    // Reusable cell styling utility to keep row-building logic concise.
    const applyStyle = (
        cell,
        { bg, fontColor = C_BLACK, bold = false, size = 11 } = {}
    ) => {
        if (bg) cell.fill = { type: "pattern", pattern: "solid", fgColor: bg }
        cell.font = { bold, color: fontColor, size }
        cell.border = borderThin
        cell.alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
        }
    }

    // Returns per-cell style based on metric type and day state (past/today/alert).
    const getDayStyle = (day, metricId) => {
        const isPast = day.PreviousDate === true
        const isToday = day.SelectedDate === true

        if (metricId === "Date") {
            if (isPast) return { bg: C_PAST, fontColor: C_GREY, bold: false }
            if (isToday)
                return {
                    bg: C_TODAY_BG,
                    fontColor: C_WHITE_TXT,
                    bold: true,
                }
            return { bg: C_BG, fontColor: C_BLACK, bold: false }
        }

        if (metricId === "OnHand") {
            if (isPast) return { bg: C_PAST, fontColor: C_GREY, bold: false }
            if (isToday) return { bg: C_WHITE, fontColor: C_BLUE, bold: true }
            return { bg: C_WHITE, fontColor: C_BLACK, bold: false }
        }

        if (metricId === "Inbound") {
            if (isPast) return { bg: C_PAST, fontColor: C_BLUE, bold: false }
            if (isToday) return { bg: C_WHITE, fontColor: C_BLUE, bold: true }
            return { bg: C_WHITE, fontColor: C_BLUE, bold: false }
        }

        if (metricId === "Planned") {
            if (isPast) return { bg: C_PAST, fontColor: C_GREY, bold: true }
            return { bg: C_PLANNED_BG, fontColor: C_BLACK, bold: true }
        }

        if (metricId === "Projected") {
            if (isPast) return { bg: C_PAST, fontColor: C_GREY, bold: true }
            return {
                bg: day.projected_cars < 0 ? C_RED : C_PROJECTED_BG,
                fontColor: C_WHITE_TXT,
                bold: true,
            }
        }

        return {}
    }

    const metrics = [
        { label: labels.common.date, key: null, id: "Date" },
        { label: labels.common.onHand, key: "TotalOnHand", id: "OnHand" },
        { label: labels.common.inBound, key: "TotalInbound", id: "Inbound" },
        { label: labels.common.planned, key: "planCount", id: "Planned" },
        {
            label: labels.common.projected,
            key: "projected_cars",
            id: "Projected",
        },
    ]

    // Build workbook and configure worksheet structure for export.
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(labels.planLbl.railCarPlan)

    ws.columns = [
        { width: 12 },
        { width: 14 },
        { width: 14 },
        { width: 12 },
        { width: 12 },
        { width: 12 },
        { width: 12 },
        { width: 12 },
        { width: 12 },
        { width: 12 },
    ]

    ws.addRow([`${labels.planLbl.railCarPlan} - ${selectedPlanName}`])
    ws.mergeCells("A1:J1")
    ws.getRow(1).height = 28
    applyStyle(ws.getCell("A1"), { bg: C_BG, bold: true, size: 18 })

    ws.addRow([`${monthStart} ${monthStartDay} - ${monthEnd} ${monthEndDay}`])
    ws.mergeCells("A2:J2")
    ws.getRow(2).height = 22
    applyStyle(ws.getCell("A2"), { bg: C_BG, bold: true, size: 13 })

    ws.addRow([])

    // Table header row for week/status/metric and weekday columns.
    const hdrRow = ws.addRow([
        labels.common.week,
        labels.common.status,
        labels.common.metric,
        ...labels.weekDays,
    ])
    hdrRow.height = 22
    hdrRow.eachCell((cell) =>
        applyStyle(cell, { bg: C_BG, bold: true, size: 11 })
    )

    for (let weekIndex = 0; weekIndex < 5; weekIndex++) {
        // Render one week block (5 metric rows) and merge week/status labels.
        const week = days.slice(weekIndex * 7, weekIndex * 7 + 7)
        const hasAlert = week.some((d) => d.projected_cars < 0)
        const statusLabel = hasAlert
            ? labels.planLbl.review
            : labels.planLbl.good
        const statusColor = hasAlert ? C_RED : C_GREEN
        const startDataRow = ws.rowCount + 1

        metrics.forEach(({ label, key, id }) => {
            const labelBg = C_BG
            const values = week.map((d) =>
                key === null
                    ? formatExportMonthDay(d.CreateTime, labels)
                    : (d[key] ?? 0)
            )
            const row = ws.addRow(["", "", label, ...values])
            row.height = 22
            applyStyle(row.getCell(3), { bg: labelBg, bold: true })
            week.forEach((day, di) => {
                applyStyle(row.getCell(4 + di), getDayStyle(day, id))
            })
        })

        const endDataRow = ws.rowCount

        ws.mergeCells(startDataRow, 1, endDataRow, 1)
        const weekCell = ws.getCell(startDataRow, 1)
        weekCell.value = `${labels.common.week} ${weekIndex + 1}`
        applyStyle(weekCell, { bg: C_BG, bold: true })

        ws.mergeCells(startDataRow, 2, endDataRow, 2)
        const statusCell = ws.getCell(startDataRow, 2)
        statusCell.value = statusLabel
        applyStyle(statusCell, {
            bg: statusColor,
            fontColor: C_WHITE_TXT,
            bold: true,
        })

        ws.addRow([])
    }

    // Convert workbook to a blob and trigger browser download.
    const buffer = await wb.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute(
        "download",
        `${labels.planLbl.calendarView}-${selectedPlanName.toLowerCase().replace(/\s+/g, "")}-${moment().format("YYYYMMDD")}.xlsx`
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

export const loadFullStoryAnalytics = () => {
    const userId = currentUser() ? currentUser().userName : ""
    const fsScript = document.getElementById("fullstoryAnalytics")
    if (!fsScript) {
        const script = document.createElement("script")
        script.innerHTML = `
    window['_fs_host'] = 'fullstory.com';
    window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
    window['_fs_org'] = 'o-1AGG19-na1';
    window['_fs_namespace'] = 'FS';    
  (function(m,n,e,t,l,o,g,y){
    if (e in m) {if(m.console && m.console.log) { m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].');} return;}
    g=m[e]=function(a,b,s){g.q?g.q.push([a,b,s]):g._api(a,b,s);};g.q=[];
    o=n.createElement(t);o.async=1;o.crossOrigin='anonymous';o.src='https://'+_fs_script;
    y=n.getElementsByTagName(t)[0];y.parentNode.insertBefore(o,y);
    g.identify=function(i,v,s){g(l,{uid:i},s);if(v)g(l,v,s)};g.setUserVars=function(v,s){g(l,v,s)};g.event=function(i,v,s){g('event',{n:i,p:v},s)};
    g.anonymize=function(){g.identify(!!0)};
    g.shutdown=function(){g("rec",!1)};g.restart=function(){g("rec",!0)};
    g.log = function(a,b){g("log",[a,b])};
    g.consent=function(a){g("consent",!arguments.length||a)};
    g.identifyAccount=function(i,v){o='account';v=v||{};v.acctId=i;g(o,v)};
    g.clearUserCookie=function(){};
    g.setVars=function(n, p){g('setVars',[n,p]);};
    g._w={};y='XMLHttpRequest';g._w[y]=m[y];y='fetch';g._w[y]=m[y];
    if(m[y])m[y]=function(){return g._w[y].apply(this,arguments)};
    g._v="1.3.0";
  })(window,document,window['_fs_namespace'],'script','user');
  FS.identify("${userId}", {
  displayName:"${userId}"});`
        script.id = "fullstoryAnalytics"
        document.head.appendChild(script)
    }
}

export const loadChatBot = () => {
    const existingScript = document.getElementById("chatBot")
    if (!existingScript) {
        const script = document.createElement("script")
        script.SameSite = "None; Secure"
        script.src = "https://static.landbot.io/landbot-3/landbot-3.0.0.js"
        script.id = "chatBot"
        document.body.appendChild(script)
        script.onload = () => {
            // eslint-disable-next-line
            var myLandbot = new window.Landbot.Livechat({
                configUrl:
                    "https://chats.landbot.io/v3/H-863650-MC8OAI324GVB00ER/index.json",
            })
        }
    }
}

export const unLoadChatBot = () => {
    const script = document.getElementById("chatBot")
    const chatWindowElement =
        document.getElementsByClassName("LandbotLivechat")[0]

    if (script) script.remove()
    if (chatWindowElement) chatWindowElement.remove()
}

export const checkRailRoad = (roadList) => {
    const { STORED_RAILROAD_NAME } = getStoredShipper()
    if (
        !(
            STORED_RAILROAD_NAME === null ||
            STORED_RAILROAD_NAME === undefined ||
            STORED_RAILROAD_NAME === ""
        )
    ) {
        if (Array.isArray(roadList)) {
            return roadList.indexOf(STORED_RAILROAD_NAME) > -1 ? true : false
        } else {
            return roadList === STORED_RAILROAD_NAME ? true : false
        }
    } else return false
}

export const getCurrencyCode = (totalCharge, currencyCode) => {
    let formattedTotalCharge = totalCharge ? totalCharge.toString() : "0.0"
    let total = ""
    switch (currencyCode) {
        case "CAD":
        case "MXN":
        case "USD":
            total = `$${formattedTotalCharge}`
            break
        case "GBP":
            total = `£${formattedTotalCharge}`
            break
        case "EUR":
            total = `€${formattedTotalCharge}`
            break
        default:
            total = formattedTotalCharge
            break
    }
    return total
}

export const dateFormatterForGrids = (params) => {
    let month = labels.monthsInitials[moment(params.value).month()]
    let date = moment(params.value).format("DD, YYYY")
    let time = moment(params.value).format("HH:mm")
    return params.value && params.value !== "" && params.value !== undefined
        ? `${month} ${date} ${time}`
        : ""
}

export const OWFormatterForGrids = (params) => {
    let Ow_value = Number(params.value)
    if (Ow_value === 0) {
        return ""
    } else if (Ow_value === 1) {
        return labels.common.no
    } else if (Ow_value === 2) {
        return labels.common.yes
    }
}
export const searchObjArray = (value, array, key) => {
    for (var i = 0; i < array.length; i++) {
        if (array[i][key] === value) {
            return array[i]
        }
    }
}

export const currentUser = () => {
    if (localStorage.getItem("okta-token-storage")) {
        if (JSON.parse(localStorage.getItem("okta-token-storage")).idToken) {
            if (
                JSON.parse(localStorage.getItem("okta-token-storage")).idToken
                    .claims
            ) {
                let claims = JSON.parse(
                    localStorage.getItem("okta-token-storage")
                ).idToken.claims
                return {
                    email: claims.email.trim()
                        ? claims.email.trim().toLowerCase()
                        : "",
                    name: claims.name.trim()
                        ? claims.name.trim().toLowerCase()
                        : "",
                    userName: claims.preferred_username.trim()
                        ? claims.preferred_username.trim().toLowerCase()
                        : "",
                }
            }
        }
    }

    return null
}

export const convertArrayToObject = (array, key) => {
    const initialValue = {}
    return array.reduce((obj, item) => {
        return {
            ...obj,
            [item[key]]: item,
        }
    }, initialValue)
}

export const getStoredShipper = () => {
    const shipper = JSON.parse(localStorage.getItem("shipper_details"))

    return {
        SHIPPER: shipper,
        STORED_SHIPPER_NAME:
            shipper && Object.keys(shipper).length ? shipper.orgName : "",
        STORED_RAILROAD_NAME:
            shipper && Object.keys(shipper).length
                ? shipper.grantors[0].orgName
                : "",
        STORED_SHIPPER_ID:
            shipper && Object.keys(shipper).length ? shipper.orgId : "",
        STORED_RAILROAD_ID:
            shipper && Object.keys(shipper).length
                ? shipper.grantors[0].orgId
                : "",
        STORED_SHIPPER_NAME_ENCODED:
            shipper && Object.keys(shipper).length
                ? encodeURIComponent(shipper.orgName)
                : "",
        STORED_RAILROAD_NAME_ENCODED:
            shipper && Object.keys(shipper).length
                ? encodeURIComponent(shipper.grantors[0].orgName)
                : "",
    }
}

export const checkUserPermission = (accesRights, pageUrl) => {
    let permitStatus = false
    let navPermissionList = {
        isSchedularViewPermitted: false,
        isCreateSchedularPermitted: false,
        isSchedularDeletePermitted: false,
        isCarOrderCreatePermitted: false,
        isCarOrderViewPermitted: false,
        isCreateBillsPermitted: false,
        isChargeManagementPermitted: false,
        isGeneralPlacementViewPermitted: false,
        isGeneralPlacementPermitted: false,
        isAssignedCarViewPermitted: false,
        isManageEmailPermitted: false,
        isSwitchRequestsPermitted: false,
        isSwitchPatternsRequired: false,
        isCareMovePermitted: false,
        isEnhancedVisibilityPermitted: false,
        isEnhancedVisibilityRedirection: false,
        isViewEnhancedVisibilityDataPermitted: false,
        isForcastingModuleAccessible: false,
        isForcastingPlanManagable: false,
    }

    if (accesRights && accesRights.permissions) {
        accesRights.permissions.forEach((item) => {
            if (
                item.permissionName ===
                    PERMISSIONS.CAN_ACCESS_ENHANCED_VISIBILITY_MODULE &&
                accesRights.permissions.length > 1
            )
                navPermissionList.isEnhancedVisibilityPermitted = true
            else if (
                item.permissionName ===
                    PERMISSIONS.CAN_ACCESS_ENHANCED_VISIBILITY_MODULE &&
                accesRights.permissions.length === 1
            )
                navPermissionList.isEnhancedVisibilityRedirection = true
            else if (
                item.permissionName === PERMISSIONS.CAN_PLACE_NEW_CAR_ORDER
            )
                navPermissionList.isCarOrderCreatePermitted = true
            else if (item.permissionName === PERMISSIONS.CAN_VIEW_CAR_ORDERS) {
                navPermissionList.isCarOrderViewPermitted = true
            } else if (item.permissionName === PERMISSIONS.CAN_CREATE_BILLS)
                navPermissionList.isCreateBillsPermitted = true
            else if (
                item.permissionName === PERMISSIONS.CAN_VIEW_CHARGE_MANAGEMENT
            )
                navPermissionList.isChargeManagementPermitted = true
            else if (
                item.permissionName ===
                PERMISSIONS.CAN_VIEW_ASSIGNED_EQUIPMENT_CAR_ORDERS
            )
                navPermissionList.isAssignedCarViewPermitted = true
            else if (
                item.permissionName === PERMISSIONS.CAN_VIEW_GENERAL_REQUEST
            )
                navPermissionList.isGeneralPlacementViewPermitted = true
            else if (
                item.permissionName === PERMISSIONS.CAN_PLACE_GENERAL_REQUEST
            )
                navPermissionList.isGeneralPlacementPermitted = true
            else if (
                item.permissionName ===
                PERMISSIONS.CAN_MANAGE_EMAIL_NOTIFICATIONS
            )
                navPermissionList.isManageEmailPermitted = true
            else if (
                item.permissionName ===
                PERMISSIONS.CAN_VIEW_ENHANCED_VISIBILITY_DATA
            )
                navPermissionList.isViewEnhancedVisibilityDataPermitted = true
            else if (
                item.permissionName === PERMISSIONS.CAN_ACCESS_FORCASTING_MODULE
            )
                navPermissionList.isForcastingModuleAccessible = true
            else if (
                item.permissionName === PERMISSIONS.CAN_MANAGE_FORECASTING_PLANS
            )
                navPermissionList.isForcastingPlanManagable = true
            else if (item.permissionName === PERMISSIONS.CAN_MOVE_CARS)
                navPermissionList.isCareMovePermitted = true
            else if (
                item.permissionName === PERMISSIONS.CAN_SUBMIT_SWITCH_REQUST
            ) {
                navPermissionList.isSwitchRequestsPermitted = true
                navPermissionList.isSwitchPatternsRequired =
                    item.permissionExt && item.permissionExt.trim() !== ""
                        ? item.permissionExt.toUpperCase() === "R"
                        : false
            }
        })

        switch (pageUrl) {
            case "planning":
                permitStatus =
                    navPermissionList.isForcastingModuleAccessible ||
                    navPermissionList.isForcastingPlanManagable ||
                    navPermissionList.isCarOrderViewPermitted ||
                    navPermissionList.isGeneralPlacementViewPermitted ||
                    navPermissionList.isSwitchRequestsPermitted
                break
            case "shipping":
                permitStatus = navPermissionList.isCreateBillsPermitted
                break
            case "chargeManagement":
                permitStatus = navPermissionList.isChargeManagementPermitted
                break
            case "forecasting":
                permitStatus =
                    navPermissionList.isForcastingModuleAccessible ||
                    navPermissionList.isForcastingPlanManagable
                break
            case "carOrders":
                permitStatus = navPermissionList.isCarOrderViewPermitted
                break
            case "generalPlacement":
                permitStatus = navPermissionList.isGeneralPlacementViewPermitted
                break
            case "ebol":
                permitStatus = navPermissionList.isCreateBillsPermitted
                break
            case "enhancedVisibility":
                permitStatus = navPermissionList.isEnhancedVisibilityPermitted
                break
            case "emailNotifications":
                permitStatus = navPermissionList.isManageEmailPermitted
                break
            case "switchRequests":
                permitStatus = navPermissionList.isSwitchRequestsPermitted
                break
            default:
                permitStatus =
                    pageUrl !== "planning" &&
                    pageUrl !== "shipping" &&
                    pageUrl !== "accessorialManagement"
                        ? true
                        : false
        }
    }
    if (pageUrl) return permitStatus
    else {
        return navPermissionList
    }
}

export const getBrowserLang = () => {
    const userLanguage = navigator.language || navigator.userLanguage

    if (userLanguage.startsWith("es")) return "es"
    else if (userLanguage.startsWith("en")) return "en"
    else return "en"
}

export const shipmentRequestOptions = (ApiStandardParameters) => {
    return {
        Paging: {
            RecordsPerPage: 10000,
            PageRequested: 1,
        },
        Filter: {
            ExtractionId: 0,
            StationTracks: [],
            Railcars: [],
            EventCitiesStates: [],
        },
        Sort: {
            Field: 1,
            Order: 1,
        },
        ApiStandardParameters: ApiStandardParameters,
    }
}

// random number between min (included) and max (not included)

export const randomNumber = (min, max) => {
    return Math.floor(Math.random() * (max - min) + min)
}

export const getRandomColor = () => {
    const max = random_colors.length + 1
    return random_colors[Math.floor(Math.random() * (max - 0) + 0)]
}

export const numberWithCommas = (params) => {
    let str = params
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        .split(".")
    return `${str[0]}${str[1] ? `.${str[1].slice(0, 2)}` : ""}`
}
export const camalize = (str) => {
    return str
        .replace(/\s(.)/g, function ($1) {
            return $1.toUpperCase()
        })
        .replace(/\s/g, "")
        .replace(/^(.)/, function ($1) {
            return $1.toLowerCase()
        })
}

export const validateEmail = (email) => {
    /* eslint-disable */
    let regex =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return regex.test(email)
}

export const ArrayHasDuplicates = (array) => {
    return new Set(array).size !== array.length
}

export const getHoldType = (type) => {
    let holdType = ""
    switch (type) {
        case 1:
            holdType = labels.holdTypeLabels["STORAGE"]
            break
        case 2:
            holdType = labels.holdTypeLabels["UNLOAD"]
            break
        case 3:
            holdType = labels.holdTypeLabels["LOAD"]
            break
        default:
            holdType = labels.holdTypeLabels["OTHER"]
            break
    }
    return holdType
}

export const getServiceType = (paramServiceType, paramHoldType) => {
    let serviceType = ""
    switch (paramServiceType) {
        case 1:
            serviceType = labels.serviceTypeLabels["DEMURRAGE"]
            break
        default:
            serviceType = ""
            break
    }
    let holdType = getHoldType(paramHoldType)
    if (holdType === labels.holdTypeLabels["OTHER"]) return serviceType
    else if (holdType === labels.holdTypeLabels["STORAGE"]) return holdType
    else return `${serviceType} - ${holdType}`
}

export const formatChargeAmount = (totalBilled, currency) => {
    if (currency) {
        let amount = getCurrencyCode(parseFloat(Number(totalBilled)), currency)

        if (typeof amount === "string") {
            let currencyCode = amount[0]
            return (
                currencyCode +
                numberWithCommas(Number(amount.substring(1)).toFixed(2))
            )
        } else return "0.00"
    } else {
        return numberWithCommas(Number(totalBilled).toFixed(2))
    }
}

export const decodeNewLineFromTMS = (value) => {
    let formattedValue = ""
    formattedValue = value
        .replace(/(\\r\\n|\\r|\\n){2,}/g, "$1")
        .replace(/(\\r\\n)/g, "\n")
    return formattedValue
}

export const telegraphDataForCar = (teledata, Railcar) => {
    let emptyCarTeleData = {
        eventRoad: "",
        eventCode: "",
        eventCity: "",
        eventState: "",
        eventDate: "",
        eta: "",
        eti: "",
    }
    if (teledata.shipments.length === 0) {
        return emptyCarTeleData
    } else if (teledata && teledata.shipments.length) {
        let carTeleData = null
        teledata.shipments.forEach((data) => {
            if (data["railCar"].trim() === Railcar.trim()) {
                carTeleData = data
            }
        })

        if (
            carTeleData &&
            (carTeleData["eventRoad"] ||
                carTeleData["eventCode"] ||
                carTeleData["eventCity"] ||
                carTeleData["eventState"] ||
                carTeleData["eventDate"] ||
                carTeleData["eta"] ||
                carTeleData["eti"])
        )
            return carTeleData
        else return emptyCarTeleData
    } else return emptyCarTeleData
}

export const getMaxDate = (date1, date2) => {
    if (!date1) {
        return date2
    } else if (!date2) {
        return date1
    } else if (date1 && date2) {
        return new Date(Math.max.apply(null, [date1, date2]))
    }
}

export const getLeastDate = (date1, date2) => {
    if (!date1) {
        return date2
    } else if (!date2) {
        return date1
    } else if (date1 && date2) {
        return new Date(Math.min.apply(null, [date1, date2]))
    }
}

export const findMaxETIandETA_TELE = (data) => {
    let Tele_eti_eta = {
        eta: [],
        eti: [],
        lastMov: [],
    }

    let date_limits_tel = {
        eta_max: "",
        eti_max: "",
        last_mov_max: "",
        last_mov_min: "",
    }
    if (data.shipments.length === 0) {
        return date_limits_tel
    } else {
        data.shipments.forEach((shipment) => {
            Tele_eti_eta.eti.push(
                shipment.eti
                    ? new Date(shipment.eti.replace(" ", "T"))
                    : new Date()
            )
            Tele_eti_eta.eta.push(
                shipment.eta
                    ? new Date(shipment.eta.replace(" ", "T"))
                    : new Date()
            )
            Tele_eti_eta.lastMov.push(
                shipment.eventDate
                    ? new Date(shipment.eventDate.replace(" ", "T"))
                    : new Date()
            )
        })

        date_limits_tel = {
            eta_max: Tele_eti_eta.eta.length
                ? new Date(Math.max.apply(null, [...Tele_eti_eta.eta]))
                : false,
            eti_max: Tele_eti_eta.eti.length
                ? new Date(Math.max.apply(null, [...Tele_eti_eta.eti]))
                : false,
            last_mov_max: Tele_eti_eta.lastMov.length
                ? new Date(Math.max.apply(null, [...Tele_eti_eta.lastMov]))
                : false,
            last_mov_min: Tele_eti_eta.lastMov.length
                ? new Date(Math.min.apply(null, [...Tele_eti_eta.lastMov]))
                : false,
        }
        return date_limits_tel
    }
}

// input dates in YYYY-MM-DD format
export const showFormattedRange = (range) => {
    if (range && range.length && range.length === 2) {
        if (range[0].toString() === range[1].toString()) {
            return `${moment(range[0]).format("MM.DD.YYYY")}`
        } else {
            return `${moment(range[0]).format("MM.DD.YYYY")} - ${moment(
                range[1]
            ).format("MM.DD.YYYY")}`
        }
    }
    return ""
}

// check if the testDate is same as fromDate and toDate or in between fromDate and toDate
// input dates in YYYY-MM-DD format
export const isSameorBetweenDate = (fromDate, toDate, testDate) => {
    if (moment(fromDate).isSame(toDate)) {
        return moment(fromDate).isSame(testDate)
    } else {
        return moment(testDate, "YYYY-MM-DD").isBetween(
            moment(fromDate, "YYYY-MM-DD"),
            moment(toDate, "YYYY-MM-DD"),
            undefined,
            "[]"
        )
    }
}

/**
 * This function checks whether time is greater or equal to current time
 * @param {*} date
 * @param {*} time
 * @returns boolean value
 */
export const validateCurrentTime = (date, time) => {
    const isInvalid =
        moment(time).isSameOrAfter(moment(), "hours") &&
        moment(time).isSameOrAfter(moment(), "minutes")
            ? false
            : moment(date).isSameOrBefore(moment(), "day")
              ? true
              : false
    return isInvalid
}

/*
 save data in IndexDB

*/
export const saveDataIndexDB = (data) => {
    const {
        DB_NAME,
        DB_VERSION,
        DB_STORE_TBL_STR,
        DB_STORE_TBL_INFO,
        READ_WRITE_MOD,
    } = getIndexDBconfig()

    const requestDB = indexedDB.open(DB_NAME, DB_VERSION)

    let db

    requestDB.onupgradeneeded = (event) => {
        if (!event.target.result.objectStoreNames.contains(DB_STORE_TBL_STR)) {
            event.target.result.createObjectStore(DB_STORE_TBL_STR, {
                keyPath: "id",
                autoIncrement: true,
            })
        }

        if (!event.target.result.objectStoreNames.contains(DB_STORE_TBL_INFO)) {
            event.target.result.createObjectStore(DB_STORE_TBL_INFO, {
                keyPath: "id",
                autoIncrement: true,
            })
        }
    }
    requestDB.onerror = () => {
        console.error(`Something went wrong in IndexedDB - ${DB_NAME}`)
    }

    requestDB.onsuccess = (event) => {
        const Shipment = data

        db = event.target.result

        const objectStoreStr = db
            .transaction([DB_STORE_TBL_STR], READ_WRITE_MOD)
            .objectStore(DB_STORE_TBL_STR)
        const objectStoreInfo = db
            .transaction([DB_STORE_TBL_INFO], READ_WRITE_MOD)
            .objectStore(DB_STORE_TBL_INFO)

        objectStoreStr.clear()
        objectStoreInfo.clear()

        if (Shipment.Shipments && Shipment.Summary && Shipment.Paging) {
            objectStoreInfo.add({
                id: 1,
                Summary: Shipment.Summary,
                Paging: Shipment.Paging,
                ExtractionId: Shipment.ExtractionId,
                ExtractionDateUtc: Shipment.ExtractionDateUtc,
            })
            Shipment.Shipments.forEach((shipment) =>
                objectStoreStr.add(shipment)
            )
        } else {
            objectStoreStr.clear()
            objectStoreInfo.clear()
        }
    }
}

/**
 * Delete the indexDB
 */
export const deleteIndexDB = () => {
    const { DB_NAME } = getIndexDBconfig()

    const deleteRequest = indexedDB.deleteDatabase(DB_NAME)
    deleteRequest.onsuccess = () => {
        console.log(`Database ${DB_NAME} deleted successfully.`)
    }
    deleteRequest.onerror = () => {
        console.log(`Error deleting Database ${DB_NAME}.`)
    }
    deleteRequest.onblocked = () => {
        console.log(`Database ${DB_NAME} deletion is blocked.`)
    }
}
/**
 * clear the indexDB shipment table
 */
export const clearIndexDB = () => {
    const { DB_NAME, DB_STORE_TBL_STR, DB_VERSION } = getIndexDBconfig()

    return new Promise((resolve, reject) => {
        const requestDB = indexedDB.open(DB_NAME, DB_VERSION)
        requestDB.onerror = () => {
            console.error(
                `Something went wrong in IndexedDB while clearing table`
            )
        }

        requestDB.onsuccess = (event) => {
            let clearRequest = null
            let objectStoreStr = null
            let db = event.target.result

            if (db.objectStoreNames.contains(DB_STORE_TBL_STR)) {
                objectStoreStr = db
                    .transaction([DB_STORE_TBL_STR], "readwrite")
                    .objectStore(DB_STORE_TBL_STR)

                clearRequest = objectStoreStr.clear()
            }
            db.close()
            if (clearRequest) {
                clearRequest.onsuccess = () => {
                    console.log(`Cleared object store ${DB_STORE_TBL_STR}`)
                }
                clearRequest.onerror = () => {
                    console.log(
                        `Failed to clear object store ${DB_STORE_TBL_STR}`
                    )
                }
            }
        }
    })
}

/**
 *
 * @returns get data from IndexDB
 */

export const getShipmentFromIndexDB = () => {
    const { DB_NAME, DB_STORE_TBL_STR, DB_STORE_TBL_INFO, DB_VERSION } =
        getIndexDBconfig()

    return new Promise((resolve, reject) => {
        const requestDB = indexedDB.open(DB_NAME, DB_VERSION)

        requestDB.onerror = () => {
            console.error(`Something went wrong in IndexedDB`)
        }

        requestDB.onsuccess = (event) => {
            let db = event.target.result
            const objectStoreStr = db
                .transaction([DB_STORE_TBL_STR])
                .objectStore(DB_STORE_TBL_STR)
            const objectStoreInfo = db
                .transaction([DB_STORE_TBL_INFO])
                .objectStore(DB_STORE_TBL_INFO)

            const requestSTR = objectStoreStr.getAll()
            const requestINFO = objectStoreInfo.get(1)

            requestSTR.onerror = () => {
                reject("Request error on store str")
            }

            requestINFO.onerror = () => {
                reject("Request error on store info")
            }

            requestSTR.onsuccess = () => {
                const ships = requestSTR.result

                requestINFO.onsuccess = () => {
                    resolve({ Shipments: ships, ...requestINFO.result })
                }
            }
        }
    })
}

/**
 * This function will return an object of thre days, day 105 days before the first day of the week,
 * first day of the week, day 105 days after the first day of the week
 * @returns {day105theBefore, firstDayOfWeek, day105theAfter}
 */

export const get105theDayBeforeAndAfter = () => {
    let currentDate = new Date()
    let dayOfWeek = currentDate && currentDate.getDay()

    let firstDayOfWeek = new Date(currentDate)
    firstDayOfWeek.setDate(currentDate.getDate() - dayOfWeek)

    let day105theBefore = new Date(firstDayOfWeek)
    let day105theAfter = new Date(firstDayOfWeek)

    day105theBefore.setDate(firstDayOfWeek.getDate() - 105)
    day105theAfter.setDate(firstDayOfWeek.getDate() + 104)

    let day105theBeforeD_date = `${day105theBefore.getFullYear()}-${String(day105theBefore.getMonth() + 1).padStart(2, "0")}-${String(day105theBefore.getDate()).padStart(2, "0")}`
    let firstDayOfWeek_date = `${firstDayOfWeek.getFullYear()}-${String(firstDayOfWeek.getMonth() + 1).padStart(2, "0")}-${String(firstDayOfWeek.getDate()).padStart(2, "0")}`
    let day105theAfter_date = `${day105theAfter.getFullYear()}-${String(day105theAfter.getMonth() + 1).padStart(2, "0")}-${String(day105theAfter.getDate()).padStart(2, "0")}`

    return { day105theBeforeD_date, firstDayOfWeek_date, day105theAfter_date }
}
